/**
 * Saving the layout being edited to the server. A save names the version it was made from; if someone
 * saved since, the server refuses it and the lead chooses what to do, nothing overwritten. Until a
 * change is saved it is kept on the device (a draft), so a closed browser or a lost connection loses
 * nothing: a failed save waits and tries again, and a save that needed a fresh sign-in goes through
 * right after it.
 */

import type { EventRow, LayoutRow, StaleSave } from '@mawkeb/shared/api';
import type { Layout } from '@mawkeb/shared/layout';
import { create } from 'zustand';
import { api, ApiError } from '../api';
import { cacheDelete, cacheGet, cacheSet } from './cache';
import { layoutState, useLayout } from './layout';
import { session } from './session';

export interface Draft {
  layoutId: string;
  eventId: string;
  name: string;
  /** The version the changes were made from. */
  base: number;
  data: Layout;
  at: number;
}

export interface CachedLayout {
  layout: LayoutRow;
  data: Layout;
  event: EventRow | null;
  at: number;
}

export const draftKey = (layoutId: string) => `draft:${layoutId}`;
export const layoutKey = (layoutId: string) => `layout:${layoutId}`;

export type SaveStatus = 'idle' | 'saving' | 'waiting' | 'conflict';

interface SavingState {
  row: LayoutRow | null;
  event: EventRow | null;
  /** The layout as last saved or opened; the editor has unsaved changes when its layout is another object. */
  saved: Layout | null;
  status: SaveStatus;
  /** Why the last save didn't go through, in plain words. */
  problem: string | null;
  conflict: StaleSave | null;
  /** An older version shown in the hall from the history, not editable. */
  looking: { version: number; data: Layout } | null;

  open: (row: LayoutRow, data: Layout, event: EventRow | null, draft?: Layout) => void;
  close: () => void;
  save: () => Promise<boolean>;
  setRow: (row: LayoutRow) => void;
  lookAt: (version: number, data: Layout) => void;
  stopLooking: () => void;
  settled: () => void;
}

let retry: ReturnType<typeof setTimeout> | null = null;
let draftTimer: ReturnType<typeof setTimeout> | null = null;

export const useSaving = create<SavingState>()((set, get) => ({
  row: null,
  event: null,
  saved: null,
  status: 'idle',
  problem: null,
  conflict: null,
  looking: null,

  open: (row, data, event, draft) => {
    if (retry) clearTimeout(retry);
    set({ row, event, saved: data, status: 'idle', problem: null, conflict: null, looking: null });
    layoutState().reset(data);
    if (draft) layoutState().commit(draft);
    void cacheSet(layoutKey(row.id), { layout: row, data, event, at: Date.now() } satisfies CachedLayout);
  },

  close: () => {
    if (retry) clearTimeout(retry);
    layoutState().setPreview(null);
    set({ row: null, event: null, saved: null, status: 'idle', problem: null, conflict: null, looking: null });
  },

  save: async () => {
    const { row, saved, status, looking } = get();
    if (!row || looking || status === 'saving' || status === 'conflict') return false;
    const data = layoutState().layout;
    if (data === saved) return true;
    if (retry) clearTimeout(retry);
    set({ status: 'saving', problem: null });
    try {
      const r = await api.saveLayout(row.id, row.version, data);
      const me = session().me;
      const next: LayoutRow = { ...row, version: r.version, savedAt: r.savedAt, savedBy: me ? { email: me.email, name: me.name } : row.savedBy };
      set({ row: next, saved: data, status: 'idle', problem: null });
      void cacheSet(layoutKey(row.id), { layout: next, data, event: get().event, at: Date.now() } satisfies CachedLayout);
      writeDraft();
      return true;
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'The save didn’t go through. Try again.');
      if (err.status === 409) {
        set({ status: 'conflict', conflict: err.body as StaleSave, problem: null });
      } else if (err.status === 401) {
        set({ status: 'waiting', problem: 'Sign in again to save. Your changes are kept on this device.' });
        session().afterSignIn(() => void get().save());
      } else if (err.status === 'offline' || err.status === 429 || (typeof err.status === 'number' && err.status >= 500)) {
        set({ status: 'waiting', problem: err.status === 'offline' ? 'No connection. It saves as soon as you’re back online; your changes are kept on this device.' : 'The server didn’t take the save. Trying again in a moment.' });
        retry = setTimeout(() => void get().save(), err.status === 'offline' ? 20_000 : 8_000);
      } else {
        set({ status: 'idle', problem: err.message });
      }
      return false;
    }
  },

  setRow: (row) => set({ row }),

  lookAt: (version, data) => {
    set({ looking: { version, data } });
    layoutState().select([]);
    layoutState().setPreview(data);
  },

  stopLooking: () => {
    set({ looking: null });
    layoutState().setPreview(null);
  },

  /** The refused save was dealt with (kept as a new layout, or theirs opened). */
  settled: () => set({ status: 'idle', conflict: null }),
}));

export const saving = useSaving.getState;

/** Whether the layout in the editor has changes not yet saved. */
export function useUnsaved(): boolean {
  const layout = useLayout((s) => s.layout);
  const saved = useSaving((s) => s.saved);
  return saved !== null && layout !== saved;
}

function writeDraft() {
  const { row, saved } = saving();
  if (!row) return;
  const now = layoutState().layout;
  if (now === saved) void cacheDelete(draftKey(row.id));
  else void cacheSet(draftKey(row.id), { layoutId: row.id, eventId: row.eventId, name: row.name, base: row.version, data: now, at: Date.now() } satisfies Draft);
}

// unsaved changes go to the device a moment after each edit
useLayout.subscribe((s, prev) => {
  if (s.layout === prev.layout || !saving().row) return;
  if (draftTimer) clearTimeout(draftTimer);
  draftTimer = setTimeout(writeDraft, 400);
});

addEventListener('online', () => {
  if (saving().status === 'waiting') void saving().save();
});

addEventListener('beforeunload', (e) => {
  const { row, saved } = saving();
  if (!row || layoutState().layout === saved) return;
  writeDraft();
  e.preventDefault();
});

export async function draftFor(layoutId: string): Promise<Draft | undefined> {
  return cacheGet<Draft>(draftKey(layoutId));
}

export async function dropDraft(layoutId: string): Promise<void> {
  await cacheDelete(draftKey(layoutId));
}
